import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import SEO, { personSchema, breadcrumbSchema } from "@/components/SEO";

const ERIKA_PORTRAIT = "/images/dr-erika-tajra.png";
const IMG_CLINIC = "/images/clinic.jpg";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] } },
};
const stagger = { visible: { transition: { staggerChildren: 0.12 } } };

/* ─── HERO ─── */
function HeroSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], [0, 100]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  return (
    <section ref={ref} data-testid="about-hero" className="relative min-h-[90vh] overflow-hidden bg-[#FDFBF7] flex items-center">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 w-full pt-28 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div style={{ opacity: textOpacity }}>
            <motion.div variants={stagger} initial="hidden" animate="visible">
              <motion.p variants={fadeUp} className="text-sm font-medium tracking-widest uppercase text-[#D4AF37] mb-4">
                About
              </motion.p>
              <motion.h1 variants={fadeUp} className="text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.1] text-[#2D2D2D] mb-6">
                Dr Erika Tajra
              </motion.h1>
              <motion.p variants={fadeUp} className="font-['Cormorant_Garamond'] italic text-xl md:text-2xl text-[#D4AF37] mb-8">
                Precision Medicine. Deep Biology. Human Health — Reimagined.
              </motion.p>
              <motion.p variants={fadeUp} className="text-lg font-light leading-relaxed text-[#6B6B6B] mb-8 max-w-lg">
                I am a medical doctor trained in the UK, specialising in personalised
                medicine, genomics, and the biology of human health across generations.
              </motion.p>
              <motion.div variants={fadeUp}>
                <p className="text-base font-light text-[#6B6B6B] mb-4">My work sits at the intersection of:</p>
                <div className="space-y-2 mb-8">
                  {[
                    "Clinical science",
                    "Genomics and bioinformatics",
                    "Microbiome science",
                    "Longevity and disease prevention",
                    "Fertility and preconception health",
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full flex-shrink-0" />
                      <span className="text-base font-light text-[#2D2D2D]">{item}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
              <motion.div variants={fadeUp}>
                <p className="text-base font-light text-[#6B6B6B] mb-4">But what I truly do is this:</p>
                <p className="text-lg font-light text-[#2D2D2D] leading-relaxed mb-1">
                  I help people understand their biology —
                </p>
                <p className="text-lg font-medium text-[#2D2D2D]">
                  so they can change the trajectory of their health.
                </p>
              </motion.div>
            </motion.div>
          </motion.div>

          <motion.div style={{ y: imageY }} className="relative hidden lg:block">
            <div className="relative">
              <div className="absolute -top-4 -right-4 w-full h-full border border-[#D4AF37]/20" />
              <img
                src={ERIKA_PORTRAIT}
                alt="Dr Erika Tajra"
                className="w-full aspect-[3/4] object-cover object-top relative z-10"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ─── MY APPROACH ─── */
function ApproachSection() {
  return (
    <section className="py-24 md:py-36 bg-[#F5F2EB]">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
          <p className="text-sm font-medium tracking-widest uppercase text-[#D4AF37] mb-4">My Approach</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-[#2D2D2D] mb-10 leading-[1.15]">
            Healthcare, Rebuilt{" "}<br className="hidden md:block" />Around the Individual
          </h2>

          <p className="text-lg font-light text-[#2D2D2D] leading-relaxed mb-8">
            Modern medicine is powerful — but fragmented.
          </p>

          <p className="text-base font-light text-[#6B6B6B] mb-4">It often addresses:</p>
          <div className="space-y-2 mb-10">
            {[
              "symptoms in isolation",
              "organs as separate systems",
              "data without integration",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#6B6B6B] rounded-full mt-2.5 flex-shrink-0" />
                <span className="text-base font-light text-[#6B6B6B]">{item}</span>
              </div>
            ))}
          </div>

          <p className="text-lg font-medium text-[#2D2D2D] mb-8">I take a different approach.</p>

          <p className="text-base font-light text-[#6B6B6B] leading-relaxed mb-2">
            I view the human body as a connected biological system —
          </p>
          <p className="text-base font-light text-[#6B6B6B] leading-relaxed mb-10">
            where genetics, environment, microbiome, metabolism, and lifestyle interact continuously.
          </p>

          <p className="text-base font-light text-[#6B6B6B] mb-4">This allows us to:</p>
          <div className="space-y-3 mb-10">
            {[
              "identify root causes",
              "understand patterns across systems",
              "move from reaction → prediction → optimisation",
            ].map((item) => (
              <div key={item} className="flex items-center gap-3">
                <div className="w-6 h-[1px] bg-[#D4AF37] flex-shrink-0" />
                <span className="text-base font-light text-[#2D2D2D]">{item}</span>
              </div>
            ))}
          </div>

          <p className="text-base font-light text-[#6B6B6B] leading-relaxed mb-1">
            This is where healthcare becomes precise.
          </p>
          <p className="text-lg font-medium text-[#2D2D2D]">And where outcomes begin to change.</p>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── MY JOURNEY ─── */
function JourneySection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], [-30, 30]);

  return (
    <section ref={ref} className="py-24 md:py-36 bg-[#FDFBF7] overflow-hidden relative">
      <motion.div style={{ y: bgY }} className="absolute top-0 right-0 w-1/3 h-full opacity-[0.03] pointer-events-none hidden lg:block">
        <img src={IMG_CLINIC} alt="" className="w-full h-full object-cover" />
      </motion.div>
      <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
          <p className="text-sm font-medium tracking-widest uppercase text-[#D4AF37] mb-4">My Journey</p>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-[#2D2D2D] mb-10">
            Why I Do This Work
          </h2>

          <p className="text-base font-light text-[#6B6B6B] leading-relaxed mb-2">
            My path into personalised medicine was not only academic —
          </p>
          <p className="text-base font-light text-[#6B6B6B] leading-relaxed mb-8">
            it was deeply personal.
          </p>

          <p className="text-lg font-light text-[#2D2D2D] leading-relaxed mb-8">
            During the COVID-19 pandemic, I experienced a miscarriage.
          </p>

          <p className="text-base font-light text-[#6B6B6B] leading-relaxed mb-6">
            At the time, I began asking deeper questions:
          </p>

          <div className="space-y-3 mb-10 pl-4 border-l-2 border-[#D4AF37]/30">
            {[
              "Why does this happen?",
              "What determines the health of an embryo so early?",
              "What influences whether a pregnancy continues… or does not?",
            ].map((q) => (
              <p key={q} className="text-base font-light text-[#2D2D2D] italic">{q}</p>
            ))}
          </div>

          <p className="text-base font-light text-[#6B6B6B] mb-4">
            This led me into an intensive exploration of:
          </p>
          <div className="space-y-2 mb-10">
            {[
              "fertility biology",
              "oxidative stress and mitochondrial function",
              "microbiome influence on immune regulation",
              "early embryonic development and epigenetics",
              "personalised approaches to cancer biology, prevention, ageing, and fertility",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mt-2.5 flex-shrink-0" />
                <span className="text-base font-light text-[#2D2D2D]">{item}</span>
              </div>
            ))}
          </div>

          <p className="text-lg font-medium text-[#2D2D2D] mb-6">
            What I discovered changed how I approach health.
          </p>

          <p className="text-base font-light text-[#6B6B6B] leading-relaxed mb-1">
            I began to understand that:
          </p>
          <p className="text-base font-light text-[#2D2D2D] leading-relaxed mb-1">
            Health outcomes are not random.
          </p>
          <p className="text-base font-light text-[#2D2D2D] leading-relaxed mb-8">
            They follow biological patterns — patterns that can be measured, understood, and influenced.
          </p>

          <p className="text-lg font-light text-[#2D2D2D] leading-relaxed">
            Today, that understanding forms the foundation of my work with clients around the world.
          </p>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── TRAINING & BACKGROUND ─── */
function TrainingSection() {
  return (
    <section className="py-24 md:py-36 bg-[#F5F2EB]">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
          <p className="text-sm font-medium tracking-widest uppercase text-[#D4AF37] mb-4">Training & Background</p>

          <div className="space-y-5 mb-12">
            {[
              "Medical Doctor (UK-trained)",
              "Master's Degree in Personalised Medicine & Novel Therapies (UCL)",
              "Clinical experience across hospital and community settings",
              "Ongoing work in genomics, microbiome science, and clinical bioinformatics",
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4">
                <div className="w-8 h-[1px] bg-[#D4AF37] mt-3 flex-shrink-0" />
                <span className="text-base font-light text-[#2D2D2D] leading-relaxed">{item}</span>
              </div>
            ))}
          </div>

          <p className="text-base font-light text-[#6B6B6B] mb-4">My work integrates:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
            {[
              "Conventional clinical science",
              "Functional and systems biology",
              "Genomics and data interpretation",
              "Microbiome-based strategies",
            ].map((item) => (
              <div key={item} className="flex items-center gap-3 bg-[#FDFBF7] p-4 border border-[rgba(45,45,45,0.06)]">
                <div className="w-2 h-2 bg-[#D4AF37] rounded-full flex-shrink-0" />
                <span className="text-sm font-light text-[#2D2D2D]">{item}</span>
              </div>
            ))}
          </div>

          <p className="text-lg font-light text-[#2D2D2D] leading-relaxed">
            This allows me to translate complex biological data into clear, actionable strategies.
          </p>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── WHO I WORK WITH ─── */
function WhoSection() {
  return (
    <section className="py-24 md:py-36 bg-[#FDFBF7]">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
          <p className="text-sm font-medium tracking-widest uppercase text-[#D4AF37] mb-4">Who I Work With</p>
          <p className="text-lg font-light text-[#2D2D2D] leading-relaxed mb-8">
            I work with individuals who want to go beyond standard medical care.
          </p>

          <p className="text-base font-light text-[#6B6B6B] mb-4">Including:</p>
          <div className="space-y-4 mb-12">
            {[
              "High-performing individuals seeking optimisation",
              "Women and men preparing for pregnancy",
              "Individuals pursuing fertility preservation (egg, sperm, embryo freezing)",
              "Individuals navigating fertility challenges",
              "Individuals with chronic, unresolved symptoms",
              "Those focused on longevity and disease prevention",
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4">
                <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mt-2.5 flex-shrink-0" />
                <span className="text-lg font-light text-[#2D2D2D] leading-relaxed">{item}</span>
              </div>
            ))}
          </div>

          <p className="text-base font-light text-[#6B6B6B] mb-4">My clients are often:</p>
          <div className="space-y-3">
            {["Highly informed", "Proactive", "Seeking deeper answers"].map((item) => (
              <div key={item} className="flex items-center gap-3">
                <ArrowRight size={16} className="text-[#D4AF37] flex-shrink-0" />
                <span className="text-base font-medium text-[#2D2D2D]">{item}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── HOW I THINK ─── */
function HowIThinkSection() {
  return (
    <section className="py-24 md:py-36 bg-[#F5F2EB]">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
          <p className="text-sm font-medium tracking-widest uppercase text-[#D4AF37] mb-4">The Biology Behind My Work</p>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-[#2D2D2D] mb-12">
            I see the body through three core lenses:
          </h2>
        </motion.div>

        <div className="space-y-0">
          {[
            {
              num: "01",
              title: "Systems Biology",
              desc: "The body functions as an interconnected network — not isolated parts.",
            },
            {
              num: "02",
              title: "Information Biology",
              desc: "Genetics and epigenetics shape how your body responds to the world.",
            },
            {
              num: "03",
              title: "Adaptive Biology",
              desc: "Your body is constantly responding to inputs: nutrition, environment, stress, microbiome, lifestyle.",
            },
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="flex gap-8 py-10 border-t border-[rgba(45,45,45,0.08)]"
            >
              <span className="text-4xl md:text-5xl font-medium text-[#E6DCCA] flex-shrink-0 w-16">
                {item.num}
              </span>
              <div>
                <h3 className="text-xl font-medium text-[#2D2D2D] mb-2">{item.title}</h3>
                <p className="text-base font-light leading-relaxed text-[#6B6B6B]">{item.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-10"
        >
          <p className="text-base font-light text-[#6B6B6B] mb-4">When we understand these layers, we can:</p>
          <div className="space-y-3">
            {["Shift physiology", "Reduce risk", "Optimise function", "Influence long-term outcomes"].map((item) => (
              <div key={item} className="flex items-center gap-3">
                <ArrowRight size={16} className="text-[#D4AF37] flex-shrink-0" />
                <span className="text-base font-medium text-[#2D2D2D]">{item}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── WHAT MAKES THIS DIFFERENT ─── */
function DifferentSection() {
  return (
    <section className="py-24 md:py-36 bg-[#FDFBF7]">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
          <p className="text-sm font-medium tracking-widest uppercase text-[#D4AF37] mb-4">What Makes This Different</p>

          <p className="text-lg font-light text-[#2D2D2D] mb-8">Most healthcare is reactive.</p>

          <p className="text-base font-light text-[#6B6B6B] mb-4">My work is:</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-12">
            {["Predictive", "Personalised", "Integrative", "Longitudinal"].map((item) => (
              <div key={item} className="text-center p-6 bg-[#F5F2EB]">
                <span className="text-base font-medium text-[#2D2D2D]">{item}</span>
              </div>
            ))}
          </div>

          <p className="text-base font-light text-[#6B6B6B] leading-relaxed mb-2">
            I do not chase isolated symptoms.
          </p>
          <p className="text-lg font-light text-[#2D2D2D] leading-relaxed mb-1">
            I decode biological systems —
          </p>
          <p className="text-lg font-medium text-[#2D2D2D]">and translate them into strategy.</p>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── FINAL NOTE ─── */
function FinalSection() {
  return (
    <section className="py-24 md:py-36 bg-[#F5F2EB]">
      <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
          <p className="text-sm font-medium tracking-widest uppercase text-[#D4AF37] mb-8">
            A Different Way to Think About Health
          </p>
          <p className="text-xl md:text-2xl font-light text-[#2D2D2D] leading-relaxed mb-3">
            Your body is not random.
          </p>
          <p className="text-lg font-light text-[#6B6B6B] leading-relaxed mb-1">Symptoms are signals.</p>
          <p className="text-lg font-light text-[#6B6B6B] leading-relaxed mb-1">Patterns exist.</p>
          <p className="text-lg font-light text-[#6B6B6B] leading-relaxed mb-8">Biology can be understood.</p>
          <p className="text-lg font-light text-[#2D2D2D] leading-relaxed mb-2">
            When you understand your biology deeply,
          </p>
          <p className="text-lg font-light text-[#2D2D2D] leading-relaxed mb-2">
            you gain the ability to shape your health — not just respond to it.
          </p>
          <p className="text-lg font-medium text-[#2D2D2D]">This is the work I do.</p>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── CTA ─── */
function CTASection() {
  return (
    <section className="py-24 md:py-36 bg-[#1C1C1C]">
      <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
          <h2 className="text-3xl md:text-5xl font-medium tracking-tight text-[#FDFBF7] mb-6">
            Work With Me
          </h2>
          <p className="text-base font-light text-[#FDFBF7]/60 leading-relaxed mb-2">
            If you are ready to understand your biology at depth —
          </p>
          <p className="text-base font-light text-[#FDFBF7]/60 leading-relaxed mb-10">
            and take a more precise, informed approach to your health:
          </p>
          <Link to="/apply" data-testid="about-cta-book" className="btn-gold inline-flex items-center gap-2 text-base">
            Apply for a Consultation <ArrowRight size={16} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── MAIN ─── */
export default function AboutPage() {
  return (
    <main data-testid="about-page">
      <SEO
        title="About"
        path="/about"
        description="Meet Dr. Erika Tajra — a UK-trained medical doctor specialising in personalised medicine, genomics, microbiome science, and longevity. Learn about her approach, training, and journey."
        jsonLd={[
          personSchema(),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "About", path: "/about" },
          ]),
        ]}
      />
      <HeroSection />
      <ApproachSection />
      <JourneySection />
      <TrainingSection />
      <WhoSection />
      <HowIThinkSection />
      <DifferentSection />
      <FinalSection />
      <CTASection />
    </main>
  );
}
